<script setup lang="ts">
/**
 * 单标的深度研究工具卡片:
 *  - 财报抽取: 支持从 TickerLab 官方财报库一键自动抽取最新指标，亦支持本地 PDF/Excel 文件上传解析
 *  - 舆情: 近期新闻情绪与 AlphaScout 深度量化汇总 (支持 7/14/30/90 天周期切换)
 *  - 流动性: 微观流动性面板 (Amihud, Roll, Corwin-Schultz, Kyle's Lambda)
 */
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled, Refresh, Document, DataAnalysis, TrendCharts, Odometer, Money } from '@element-plus/icons-vue'
import {
  parseFinancialReport,
  extractOnlineFinancialReport,
  symbolSentiment,
  analyzeLiquidity,
  testLVarStress,
  backtestLiquidityCapacity,
  type ParseReportResponse,
  type SymbolSentimentResponse,
  type LiquidityMetrics,
  type LVarStressTestResponse,
  type CapacityBacktestResponse,
} from '@/api/client'
import { useI18nLite } from '@/composables/useI18n'
import { fmtMoney, fmtPercent } from '@/utils/format'
import { sentimentLabel } from '@/utils/labels'
import { useLatestRequest } from '@/composables/useLatestRequest'

const props = defineProps<{
  symbol: string | null
  initialResearch?: Record<string, any> | null
}>()
const { t } = useI18nLite()

const activeTab = ref('report')

// ===== 1. 财报抽取 =====
const parsing = ref(false)
const onlineLoading = ref(false)
const parseResult = ref<ParseReportResponse | null>(null)
const showUploadSection = ref(false)

const fieldNameMap: Record<string, string> = {
  report_period: '最新报告期',
  publish_date: '披露日期',
  revenue: '营业总收入',
  net_profit: '归母净利润',
  operating_profit: '营业利润',
  eps: '每股收益 (EPS)',
  roe: '净资产收益率 (ROE)',
  gross_margin: '销售毛利率',
  net_margin: '销售净利率',
  debt_ratio: '资产负债率',
  current_ratio: '流动比率',
  quick_ratio: '速动比率',
  cash_ratio: '现金比率',
  operating_cash_flow: '经营性现金流净额',
}

const latestOnlineReport = useLatestRequest()

async function loadOnlineReport() {
  if (!props.symbol) return
  // 快速切换标的时, 旧标的的慢响应不得覆盖新标的 (ALENS-107 #5)
  const isLatest = latestOnlineReport.next()
  onlineLoading.value = true
  try {
    const res = await extractOnlineFinancialReport(props.symbol)
    if (!isLatest()) return
    parseResult.value = res
    if (!res.success) {
      ElMessage.info('TickerLab 暂未查询到该标的披露数据，可尝试手动上传财报解析')
    }
  } catch (e: any) {
    if (!isLatest()) return
    ElMessage.error(`获取 TickerLab 在线财报失败: ${e?.message || t('common.networkError')}`)
  } finally {
    if (isLatest()) onlineLoading.value = false
  }
}

async function handleUpload(options: any) {
  parsing.value = true
  parseResult.value = null
  try {
    parseResult.value = await parseFinancialReport(options.file as File)
    if (!parseResult.value.success) {
      ElMessage.warning(t('research.report.failed'))
    } else {
      ElMessage.success('财报文件解析成功')
    }
  } catch (e: any) {
    ElMessage.error(`${t('research.report.failed')}: ${e?.message || t('common.networkError')}`)
  } finally {
    parsing.value = false
  }
}

const parsedFieldRows = computed(() => {
  const fields = parseResult.value?.fields || {}
  return Object.entries(fields).map(([k, v]) => {
    let formattedVal = v?.value ?? '-'
    if (typeof v?.value === 'number') {
      if (['revenue', 'net_profit', 'operating_profit', 'operating_cash_flow'].includes(k)) {
        formattedVal = fmtMoney(v.value)
      } else if (['roe', 'gross_margin', 'net_margin', 'debt_ratio'].includes(k)) {
        formattedVal = `${v.value}%`
      } else if (['eps'].includes(k)) {
        formattedVal = `${v.value} 元/股`
      }
    }
    return {
      key: k,
      label: fieldNameMap[k] || v?.raw_match || k,
      value: formattedVal,
      unit: v?.unit || '',
      confidence: v?.confidence,
    }
  })
})

// ===== 2. 舆情 =====
const sentimentLoading = ref(false)
const sentimentDays = ref(30)
const sentiment = ref<SymbolSentimentResponse | null>(null)

async function runSentiment() {
  if (!props.symbol) return
  sentimentLoading.value = true
  try {
    sentiment.value = await symbolSentiment(props.symbol, sentimentDays.value)
  } catch (e: any) {
    ElMessage.error(`${t('research.sentiment.failed')}: ${e?.message || t('common.networkError')}`)
  } finally {
    sentimentLoading.value = false
  }
}

const sentimentColor = (label?: string) => {
  if (label === 'positive') return '#34a853'
  if (label === 'negative') return '#ea4335'
  return '#909399'
}

// ===== 3. 流动性 =====
const liquidityLoading = ref(false)
const liquidity = ref<LiquidityMetrics | null>(null)

async function runLiquidity() {
  if (!props.symbol) return
  liquidityLoading.value = true
  try {
    const rows = await analyzeLiquidity([props.symbol])
    liquidity.value = rows[0] || null
  } catch (e: any) {
    ElMessage.error(`${t('research.liquidity.failed')}: ${e?.message || t('common.networkError')}`)
  } finally {
    liquidityLoading.value = false
  }
}

// 3.1 L-VaR 压力测试
const lvarLoading = ref(false)
const lvarResult = ref<LVarStressTestResponse | null>(null)
const lvarForm = reactive({
  portfolioValue: 1000000,
  liquidationDays: 5,
  maxDailyAdvPct: 10,
  confidenceLevel: 0.95,
})

async function runLVarStress() {
  if (!props.symbol) return
  lvarLoading.value = true
  try {
    lvarResult.value = await testLVarStress({
      symbols: [props.symbol],
      portfolio_value: lvarForm.portfolioValue,
      liquidation_days: lvarForm.liquidationDays,
      // 表单为百分比 (10 = 10%), 后端契约是比例 (0.1 = 10% × ADV/日)
      max_daily_adv_pct: lvarForm.maxDailyAdvPct / 100,
      confidence_level: lvarForm.confidenceLevel,
    })
  } catch (e: any) {
    ElMessage.error(`L-VaR 压力测试失败: ${e?.message || t('common.networkError')}`)
  } finally {
    lvarLoading.value = false
  }
}

// 3.2 策略流动性容量回测
const capacityLoading = ref(false)
const capacityResult = ref<CapacityBacktestResponse | null>(null)
const capacityForm = reactive({
  baseAnnualReturn: 0.25,
  baseSharpe: 1.8,
  baseMaxDd: 0.15,
  annualTurnoverTimes: 12,
})

async function runCapacityBacktest() {
  capacityLoading.value = true
  try {
    capacityResult.value = await backtestLiquidityCapacity({
      base_annual_return: capacityForm.baseAnnualReturn,
      base_sharpe: capacityForm.baseSharpe,
      base_max_dd: capacityForm.baseMaxDd,
      annual_turnover_times: capacityForm.annualTurnoverTimes,
      avg_adv: liquidity.value?.avg_daily_amount || 50000000,
    })
  } catch (e: any) {
    ElMessage.error(`容量回测失败: ${e?.message || t('common.networkError')}`)
  } finally {
    capacityLoading.value = false
  }
}

// 自动随标的与工作流预计算数据联动
watch(
  () => [props.symbol, props.initialResearch] as const,
  ([newSym, initRes]) => {
    if (!newSym) return
    if (initRes) {
      if (initRes.liquidity) liquidity.value = initRes.liquidity as any
      else runLiquidity()

      if (initRes.sentiment) sentiment.value = initRes.sentiment as any
      else runSentiment()

      // 工作流已预计算 deep_research (含财务/舆情/流动性) 时, 跳过自动拉取在线财报,
      // 仅在用户主动点击"从 TickerLab 重新抽取"时刷新
    } else {
      loadOnlineReport()
      runSentiment()
      runLiquidity()
    }
  },
  { immediate: true }
)
</script>

<template>
  <el-card shadow="hover" class="research-card">
    <template #header>
      <div class="card-header-bar">
        <div class="card-header-title">
          <el-icon class="header-icon"><DataAnalysis /></el-icon>
          <span>深度研究工具箱</span>
          <el-tag v-if="symbol" size="small" type="primary" effect="plain" style="margin-left: 8px;">
            {{ symbol }}
          </el-tag>
        </div>
        <span class="card-header-desc">直连 TickerLab 官方财报库与 AlphaScout 舆情引擎</span>
      </div>
    </template>

    <el-tabs v-model="activeTab">
      <!-- 财报抽取 Tab -->
      <el-tab-pane name="report">
        <template #label>
          <span><el-icon><Document /></el-icon> 财报关键指标抽取</span>
        </template>

        <div class="report-action-bar">
          <div class="report-meta-info" v-if="parseResult?.report_period">
            <span class="info-tag">📊 来源: <b>{{ parseResult.source_type === 'tickerlab_online' ? 'TickerLab 在线财报库' : '本地文件解析' }}</b></span>
            <span class="info-tag" v-if="parseResult.report_period && parseResult.report_period !== '-'">
              报告期: <b>{{ parseResult.report_period }}</b>
            </span>
            <span class="info-tag" v-if="parseResult.publish_date && parseResult.publish_date !== '-'">
              披露日: <b>{{ parseResult.publish_date }}</b>
            </span>
          </div>
          <div class="report-btns">
            <el-button
              size="small"
              type="primary"
              :loading="onlineLoading"
              :icon="Refresh"
              @click="loadOnlineReport"
              :disabled="!symbol"
            >
              从 TickerLab 重新抽取
            </el-button>
            <el-button
              size="small"
              type="info"
              link
              @click="showUploadSection = !showUploadSection"
            >
              {{ showUploadSection ? '收起本地上传' : '📁 上传自定义财报(PDF/Excel)' }}
            </el-button>
          </div>
        </div>

        <!-- 本地上传区域 (可选备用) -->
        <el-collapse-transition>
          <div v-show="showUploadSection" class="upload-container">
            <el-upload
              drag
              :auto-upload="true"
              :show-file-list="false"
              :http-request="handleUpload"
              accept=".pdf,.xlsx,.xls,.csv"
            >
              <el-icon size="28"><UploadFilled /></el-icon>
              <div class="el-upload__text" style="font-size: 13px;">{{ t('research.report.upload') }}</div>
              <template #tip>
                <div class="el-upload__tip">{{ t('research.report.tip') }}</div>
              </template>
            </el-upload>
          </div>
        </el-collapse-transition>

        <div v-if="parsing || onlineLoading" v-loading="true" class="loading-box" />

        <template v-if="parseResult && parsedFieldRows.length">
          <el-alert
            v-if="parseResult.warnings?.length"
            type="warning"
            :closable="false"
            style="margin: 10px 0;"
            :title="`${t('research.report.warnings')}: ${parseResult.warnings.join('; ')}`"
          />
          <el-table :data="parsedFieldRows" size="small" max-height="360" stripe>
            <el-table-column prop="label" label="指标名称" min-width="160" />
            <el-table-column label="抽取数值" min-width="140">
              <template #default="{ row }">
                <b style="color: var(--text-primary);">{{ row.value }}</b>
              </template>
            </el-table-column>
            <el-table-column prop="key" label="字段标识 (Key)" min-width="130">
              <template #default="{ row }">
                <code class="code-key">{{ row.key }}</code>
              </template>
            </el-table-column>
            <el-table-column label="置信度" width="90" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="success" effect="light" v-if="row.confidence != null">
                  {{ fmtPercent(row.confidence, 0) }}
                </el-tag>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </template>
        <div v-else-if="!onlineLoading && !parsing" class="empty-hint">
          <span>暂无财报抽取结果，请点击上方按钮从 TickerLab 抽取或手动上传财报文件。</span>
        </div>
      </el-tab-pane>

      <!-- 舆情 Tab -->
      <el-tab-pane name="sentiment" :disabled="!symbol">
        <template #label>
          <span><el-icon><TrendCharts /></el-icon> 舆情与市场情绪</span>
        </template>

        <div v-if="!symbol" class="no-symbol">{{ t('research.noSymbol') }}</div>
        <template v-else>
          <div class="sentiment-control-bar">
            <div class="period-picker">
              <span class="label-text">时间窗口:</span>
              <el-radio-group v-model="sentimentDays" size="small" @change="runSentiment">
                <el-radio-button :value="7">7天</el-radio-button>
                <el-radio-button :value="14">14天</el-radio-button>
                <el-radio-button :value="30">30天</el-radio-button>
                <el-radio-button :value="90">90天</el-radio-button>
              </el-radio-group>
            </div>
            <div class="action-right">
              <el-tag v-if="sentiment?.source === 'alphascout'" size="small" type="success" effect="light">
                ⚡ AlphaScout 舆情引擎
              </el-tag>
              <el-tag v-else-if="sentiment?.source === 'tickerlab' || sentiment?.source === 'news_articles'" size="small" type="primary" effect="light">
                📰 TickerLab 新闻聚合
              </el-tag>
              <el-button size="small" type="primary" :icon="Refresh" :loading="sentimentLoading" @click="runSentiment">
                刷新分析
              </el-button>
            </div>
          </div>

          <div v-if="sentimentLoading" v-loading="true" class="loading-box" />

          <template v-else-if="sentiment">
            <div v-if="sentiment.news_count === 0" class="empty-hint" style="margin-top: 12px;">
              {{ sentiment.note || t('research.sentiment.noNews') }}
            </div>
            <template v-else>
              <div class="sentiment-summary">
                <span class="sum-item">有效舆情样本: <b>{{ sentiment.news_count }}</b> 条</span>
                <span class="sum-item">
                  整体情绪研判:
                  <b :style="{ color: sentimentColor(sentiment.summary?.overall_label) }">
                    {{ sentimentLabel(sentiment.summary?.overall_label) }} ({{ sentiment.summary?.overall_score ?? '-' }})
                  </b>
                </span>
                <span v-if="sentiment.summary?.positive_count != null" class="sum-breakdown">
                  看多: <b style="color: #34a853;">{{ sentiment.summary?.positive_count }}</b> /
                  看空: <b style="color: #ea4335;">{{ sentiment.summary?.negative_count }}</b> /
                  中性: <b>{{ sentiment.summary?.neutral_count }}</b>
                </span>
              </div>
              <div class="news-list">
                <div v-for="item in (sentiment.items || []).slice(0, 10)" :key="item.title" class="news-row">
                  <span class="dot" :style="{ background: sentimentColor(item.label) }" />
                  <div class="news-content-col">
                    <span class="news-title">{{ item.title }}</span>
                    <div v-if="item.reasoning" class="news-reasoning">
                      {{ item.reasoning }}
                    </div>
                  </div>
                  <span class="news-score" :style="{ color: sentimentColor(item.label) }">
                    {{ item.score > 0 ? `+${item.score}` : item.score }}
                  </span>
                </div>
              </div>
            </template>
          </template>
        </template>
      </el-tab-pane>

      <!-- 流动性 Tab -->
      <el-tab-pane name="liquidity" :disabled="!symbol">
        <template #label>
          <span><el-icon><DataAnalysis /></el-icon> 微观流动性深度指标</span>
        </template>

        <div v-if="!symbol" class="no-symbol">{{ t('research.noSymbol') }}</div>
        <template v-else>
          <div class="liquidity-bar">
            <el-button size="small" type="primary" :icon="Refresh" :loading="liquidityLoading" @click="runLiquidity">
              重新计算流动性
            </el-button>
            <span class="liq-tip">基于近 20 交易日成交量、高低价价差与 Amihud 冲击模型</span>
          </div>

          <div v-if="liquidityLoading" v-loading="true" class="loading-box" />

          <template v-else-if="liquidity">
            <el-alert
              type="info"
              :closable="false"
              style="margin: 10px 0;"
              :title="t('research.liquidity.note')"
            />
            <el-descriptions :column="3" size="small" border class="liq-descriptions">
              <el-descriptions-item label="综合流动性评分">
                <b style="font-size: 16px; color: var(--accent);">{{ liquidity.liquidity_score }}</b> / 100
              </el-descriptions-item>
              <el-descriptions-item label="流动性评级">
                <el-tag size="small" :type="liquidity.liquidity_rating === '极高' || liquidity.liquidity_rating === '高' ? 'success' : 'warning'">
                  {{ liquidity.liquidity_rating }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="日均成交额 (ADV)">
                <b>{{ fmtMoney(liquidity.avg_daily_amount) }}</b>
              </el-descriptions-item>
              <el-descriptions-item label="日均换手率">
                {{ fmtPercent(liquidity.avg_turnover_rate, 2, { alreadyPercent: true }) }}
              </el-descriptions-item>
              <el-descriptions-item label="Amihud 非流动性指标">
                <code>{{ liquidity.amihud_illiq?.toFixed?.(6) ?? liquidity.amihud_illiq }}</code>
              </el-descriptions-item>
              <el-descriptions-item label="Corwin-Schultz 估算价差">
                <code>{{ liquidity.corwin_schultz_spread != null ? (liquidity.corwin_schultz_spread.toFixed(4) + '%') : '--' }}</code>
              </el-descriptions-item>
            </el-descriptions>

            <!-- L-VaR 压力测试与容量测算扩展卡片 -->
            <div class="liq-sub-sections">
              <el-row :gutter="16">
                <!-- 1. L-VaR 压力测试 -->
                <el-col :span="24" :lg="12">
                  <el-card shadow="never" class="liq-sub-card">
                    <template #header>
                      <div class="sub-card-header">
                        <span class="sub-card-title"><el-icon><Odometer /></el-icon> L-VaR 流动性调整风险压力测试</span>
                      </div>
                    </template>
                    <el-form :inline="true" size="small" class="liq-form">
                      <el-form-item label="头寸规模">
                        <el-input-number v-model="lvarForm.portfolioValue" :step="500000" :min="10000" style="width: 130px;" />
                      </el-form-item>
                      <el-form-item label="平仓天数">
                        <el-input-number v-model="lvarForm.liquidationDays" :min="1" :max="30" style="width: 75px;" />
                      </el-form-item>
                      <el-form-item label="单日ADV上限%">
                        <el-input-number v-model="lvarForm.maxDailyAdvPct" :min="1" :max="50" style="width: 75px;" />
                      </el-form-item>
                      <el-form-item>
                        <el-button type="primary" :loading="lvarLoading" @click="runLVarStress">
                          执行测算
                        </el-button>
                      </el-form-item>
                    </el-form>

                    <div v-if="lvarResult" class="lvar-results">
                      <div class="lvar-stat-grid">
                        <div class="stat-pill">
                          <span class="stat-lbl">基准 VaR (95%)</span>
                          <span class="stat-val">{{ fmtPercent(lvarResult.base_var_pct, 2, { alreadyPercent: true }) }} ({{ fmtMoney(lvarResult.base_var_amount) }})</span>
                        </div>
                        <div class="stat-pill highlight-pill">
                          <span class="stat-lbl">流动性调整 L-VaR</span>
                          <span class="stat-val highlight-val">{{ fmtPercent(lvarResult.l_var_pct, 2, { alreadyPercent: true }) }} ({{ fmtMoney(lvarResult.l_var_amount) }})</span>
                        </div>
                        <div class="stat-pill">
                          <span class="stat-lbl">清算冲击折价成本</span>
                          <span class="stat-val text-warning">{{ fmtPercent(lvarResult.liquidation_haircut_pct, 2, { alreadyPercent: true }) }}</span>
                        </div>
                        <div class="stat-pill">
                          <span class="stat-lbl">实际清算周期</span>
                          <span class="stat-val">{{ lvarResult.liquidation_days }} 交易日</span>
                        </div>
                      </div>

                      <div class="schedule-title">逐日抛售清算进度与冲击排期:</div>
                      <el-table :data="lvarResult.liquidation_schedule" size="small" max-height="220" stripe>
                        <el-table-column prop="day" label="第N日" width="65" align="center" />
                        <el-table-column label="当日清算" min-width="95">
                          <template #default="{ row }">{{ fmtMoney(row.liquidated_amount) }}</template>
                        </el-table-column>
                        <el-table-column label="剩余头寸" min-width="95">
                          <template #default="{ row }">{{ fmtMoney(row.remaining_amount) }}</template>
                        </el-table-column>
                        <el-table-column label="预估滑点" width="85" align="center">
                          <template #default="{ row }">{{ fmtPercent(row.estimated_slippage_pct, 2, { alreadyPercent: true }) }}</template>
                        </el-table-column>
                        <el-table-column label="折价成本" min-width="85">
                          <template #default="{ row }">{{ fmtMoney(row.haircut_cost) }}</template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </el-card>
                </el-col>

                <!-- 2. 策略流动性容量回测 -->
                <el-col :span="24" :lg="12">
                  <el-card shadow="never" class="liq-sub-card">
                    <template #header>
                      <div class="sub-card-header">
                        <span class="sub-card-title"><el-icon><Money /></el-icon> 策略流动性容量与滑点侵蚀测算</span>
                      </div>
                    </template>
                    <el-form :inline="true" size="small" class="liq-form">
                      <el-form-item label="基准年化">
                        <el-input-number v-model="capacityForm.baseAnnualReturn" :step="0.05" :min="0.01" :max="2.0" style="width: 85px;" />
                      </el-form-item>
                      <el-form-item label="基准夏普">
                        <el-input-number v-model="capacityForm.baseSharpe" :step="0.1" :min="0.1" :max="10.0" style="width: 75px;" />
                      </el-form-item>
                      <el-form-item label="年换手倍数">
                        <el-input-number v-model="capacityForm.annualTurnoverTimes" :step="1" :min="1" :max="100" style="width: 75px;" />
                      </el-form-item>
                      <el-form-item>
                        <el-button type="primary" :loading="capacityLoading" @click="runCapacityBacktest">
                          测算容量
                        </el-button>
                      </el-form-item>
                    </el-form>

                    <div v-if="capacityResult" class="capacity-results">
                      <div class="capacity-banner">
                        <span class="cap-lbl">最优管理容量估算:</span>
                        <span class="cap-val">{{ fmtMoney(capacityResult.optimal_capacity) }}</span>
                        <span class="cap-sub">(超过此规模后收益滑点侵蚀将加速)</span>
                      </div>

                      <div class="schedule-title">各资金梯队衰减透视:</div>
                      <el-table :data="capacityResult.capacity_levels" size="small" max-height="220" stripe>
                        <el-table-column prop="capital_label" label="资金规模" min-width="85" />
                        <el-table-column label="预期年化" min-width="85">
                          <template #default="{ row }">{{ fmtPercent(row.annualized_return, 2, { alreadyPercent: true }) }}</template>
                        </el-table-column>
                        <el-table-column label="衰减夏普" width="75" align="center">
                          <template #default="{ row }">{{ row.sharpe_ratio?.toFixed?.(2) ?? row.sharpe_ratio }}</template>
                        </el-table-column>
                        <el-table-column label="平均滑点" width="75" align="center">
                          <template #default="{ row }">{{ fmtPercent(row.avg_slippage_pct, 4, { alreadyPercent: true }) }}</template>
                        </el-table-column>
                        <el-table-column label="收益留存率" width="85" align="center">
                          <template #default="{ row }">
                            <el-tag size="small" :type="row.capacity_retention_rate >= 80 ? 'success' : row.capacity_retention_rate >= 50 ? 'warning' : 'danger'">
                              {{ fmtPercent(row.capacity_retention_rate, 1, { alreadyPercent: true }) }}
                            </el-tag>
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </el-card>
                </el-col>
              </el-row>
            </div>
          </template>
        </template>
      </el-tab-pane>
    </el-tabs>
  </el-card>
</template>

<style scoped>
.research-card { margin-top: 12px; }
.card-header-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.card-header-title { display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 15px; color: var(--text-primary); }
.header-icon { font-size: 18px; color: var(--accent); }
.card-header-desc { font-size: 12px; color: var(--text-muted); }

.report-action-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid var(--border-color); }
.report-meta-info { display: flex; align-items: center; gap: 14px; font-size: 12px; color: var(--text-secondary); }
.info-tag b { color: var(--text-primary); }
.report-btns { display: flex; align-items: center; gap: 10px; }

.upload-container { margin: 10px 0 16px; }
.loading-box { height: 80px; }
.code-key { background: var(--code-bg); color: var(--code-color); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 11px; }

.empty-hint { color: var(--text-muted); font-size: 13px; padding: 24px 0; text-align: center; }
.no-symbol { color: var(--text-muted); font-size: 13px; padding: 20px 0; text-align: center; }

.sentiment-control-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.period-picker { display: flex; align-items: center; gap: 8px; }
.label-text { font-size: 12px; color: var(--text-secondary); }
.action-right { display: flex; align-items: center; gap: 10px; }

.sentiment-summary { display: flex; align-items: center; flex-wrap: wrap; gap: 18px; margin: 10px 0 12px; font-size: 13px; background: var(--bg-tertiary); padding: 10px 14px; border-radius: 6px; }
.sum-item { color: var(--text-primary); }
.sum-breakdown { color: var(--text-secondary); font-size: 12px; margin-left: auto; }

.news-list { display: flex; flex-direction: column; }
.news-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px dashed var(--border-color); font-size: 13px; }
.news-row .dot { width: 8px; height: 8px; border-radius: 50%; flex: none; margin-top: 6px; }
.news-content-col { flex: 1; overflow: hidden; display: flex; flex-direction: column; gap: 2px; }
.news-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; color: var(--text-primary); }
.news-reasoning { font-size: 11px; color: var(--text-muted); line-height: 1.4; }
.news-score { flex: none; font-weight: 700; font-family: monospace; font-size: 13px; }

.liquidity-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.liq-tip { font-size: 12px; color: var(--text-muted); }
.liq-descriptions { margin-top: 10px; }

.liq-sub-sections { margin-top: 16px; }
.liq-sub-card { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 8px; margin-bottom: 12px; }
.sub-card-header { display: flex; align-items: center; justify-content: space-between; }
.sub-card-title { font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; color: var(--text-primary); }
.liq-form { margin-bottom: 10px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.lvar-results, .capacity-results { margin-top: 10px; }
.lvar-stat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 12px; }
.stat-pill { background: var(--bg-tertiary); padding: 8px 10px; border-radius: 6px; display: flex; flex-direction: column; gap: 4px; }
.stat-lbl { font-size: 11px; color: var(--text-secondary); }
.stat-val { font-size: 12px; font-weight: 600; color: var(--text-primary); }
.highlight-pill { border-left: 3px solid var(--accent); }
.highlight-val { color: var(--accent); }
.text-warning { color: #e6a23c; }
.schedule-title { font-size: 12px; font-weight: 600; margin: 10px 0 6px; color: var(--text-secondary); }
.capacity-banner { background: var(--bg-tertiary); padding: 10px 14px; border-radius: 6px; margin-bottom: 10px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cap-lbl { font-size: 13px; color: var(--text-secondary); }
.cap-val { font-size: 16px; font-weight: 700; color: #34a853; }
.cap-sub { font-size: 11px; color: var(--text-muted); }
</style>
